import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getCotisations, payCotisation, getExportCotisationsUrl, createPaymentCheckout } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Receipt,
  Euro,
  CheckCircle,
  Clock,
  Search,
  Filter,
  Truck,
  Calendar,
  X,
  Download,
  CreditCard,
  Loader2,
  Gift,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import MontantCotisation from "../components/MontantCotisation";

export default function CotisationsPage() {
  const [cotisations, setCotisations] = useState([]);
  const [filteredCotisations, setFilteredCotisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterYear, setFilterYear] = useState("all");
  const [payTarget, setPayTarget] = useState(null);
  const [payingOnline, setPayingOnline] = useState(null);
  const { isAdmin, user } = useAuth();

  // Get unique years from data
  const allYears = [...new Set(cotisations.map(c => c.annee))].sort((a, b) => b - a);

  useEffect(() => {
    loadCotisations();
  }, []);

  useEffect(() => {
    let filtered = cotisations;

    if (search) {
      filtered = filtered.filter((c) =>
        c.nom_food_truck.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (filterStatus !== "all") {
      filtered = filtered.filter((c) =>
        filterStatus === "paye" ? (c.statut === "paye" || c.statut === "offert") : c.statut === "non_paye"
      );
    }

    if (filterYear !== "all") {
      filtered = filtered.filter((c) => c.annee === parseInt(filterYear));
    }

    setFilteredCotisations(filtered);
  }, [search, filterStatus, filterYear, cotisations]);

  const activeFiltersCount = (search ? 1 : 0) + (filterStatus !== "all" ? 1 : 0) + (filterYear !== "all" ? 1 : 0);

  const resetFilters = () => {
    setSearch("");
    setFilterStatus("all");
    setFilterYear("all");
  };

  const loadCotisations = async () => {
    try {
      const response = await getCotisations();
      setCotisations(response.data);
      setFilteredCotisations(response.data);
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async () => {
    if (!payTarget) return;
    try {
      await payCotisation(payTarget);
      toast.success("Cotisation marquée comme payée");
      setPayTarget(null);
      loadCotisations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
  
  const handleCleanupDuplicates = async () => {
    try {
      setCleaningDuplicates(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cotisations/cleanup-duplicates`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      const result = await response.json();
      
      if (result.status === "success") {
        toast.success(result.message);
        if (result.details?.length > 0) {
          result.details.slice(0, 5).forEach(d => toast.info(d));
        }
        loadCotisations();
      } else {
        toast.error(result.detail || "Erreur");
      }
    } catch (error) {
      toast.error("Erreur lors du nettoyage: " + error.message);
    } finally {
      setCleaningDuplicates(false);
    }
  };

  const handlePayOnline = async (cotisation) => {
    setPayingOnline(cotisation.id);
    try {
      const response = await createPaymentCheckout({
        package_id: "cotisation_annuelle",
        origin_url: window.location.origin,
        cotisation_id: cotisation.id,
        food_truck_id: cotisation.food_truck_id
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Erreur lors de la création du paiement");
      setPayingOnline(null);
    }
  };

  const getStats = () => {
    const total = cotisations.reduce((sum, c) => sum + c.montant, 0);
    const paye = cotisations.filter((c) => c.statut === "paye" || c.statut === "offert").reduce((sum, c) => sum + c.montant, 0);
    const offert = cotisations.filter((c) => c.statut === "offert").length;
    const nonPaye = cotisations.filter((c) => c.statut === "non_paye").reduce((sum, c) => sum + c.montant, 0);
    return { total, paye, nonPaye, offert };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cotisations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-primary">
            Cotisations
          </h1>
          <p className="text-muted-foreground mt-1">
            Suivi des cotisations annuelles des membres
          </p>
        </div>
        {isAdmin() && (
          <>
            <Button
              variant="outline"
              onClick={() => {
                const token = localStorage.getItem("token");
                window.open(`${getExportCotisationsUrl()}?token=${token}`, "_blank");
              }}
              className="border-[#E5E0DC]"
              data-testid="export-cotisations-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter Excel
            </Button>
            <Button
              variant="outline"
              onClick={handleCleanupDuplicates}
              disabled={cleaningDuplicates}
              className="border-orange-500 text-orange-600 hover:bg-orange-50"
              data-testid="cleanup-cotisations-btn"
              title="Supprime les cotisations en double"
            >
              {cleaningDuplicates ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              {cleaningDuplicates ? "Nettoyage..." : "Nettoyer doublons (150€)"}
            </Button>
          </>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <Receipt className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total cotisations</p>
                <p className="text-2xl font-heading font-bold text-secondary">{stats.total.toFixed(2)} €</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-status-success/10">
                <CheckCircle className="w-6 h-6 text-status-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Perçues</p>
                <p className="text-2xl font-heading font-bold text-status-success">{stats.paye.toFixed(2)} €</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-status-warning/10">
                <Clock className="w-6 h-6 text-status-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">En attente</p>
                <p className="text-2xl font-heading font-bold text-status-warning">{stats.nonPaye.toFixed(2)} €</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Rechercher un food truck..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-[#E5E0DC] bg-white"
              data-testid="search-cotisations"
            />
          </div>
          
          {/* Year Filter */}
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[140px] border-[#E5E0DC]" data-testid="filter-year">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Année" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {allYears.map((year) => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex gap-2">
            <Button
              variant={filterStatus === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterStatus("all")}
              className={filterStatus === "all" ? "bg-primary" : "border-[#E5E0DC]"}
              data-testid="filter-all"
            >
              Tous
            </Button>
            <Button
              variant={filterStatus === "non_paye" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterStatus("non_paye")}
              className={filterStatus === "non_paye" ? "bg-status-warning" : "border-[#E5E0DC]"}
              data-testid="filter-unpaid"
            >
              <Clock className="w-4 h-4 mr-1" /> En attente
            </Button>
            <Button
              variant={filterStatus === "paye" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterStatus("paye")}
              className={filterStatus === "paye" ? "bg-status-success" : "border-[#E5E0DC]"}
              data-testid="filter-paid"
            >
              <CheckCircle className="w-4 h-4 mr-1" /> Payées
            </Button>
          </div>

          {activeFiltersCount > 0 && (
            <Button variant="ghost" onClick={resetFilters} className="text-muted-foreground" data-testid="reset-filters-btn">
              <X className="w-4 h-4 mr-2" />
              Réinitialiser
            </Button>
          )}
        </div>

        {/* Active filters summary */}
        {activeFiltersCount > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="active-filters-summary">
            {search && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Recherche: "{search}"
                <X className="w-3 h-3 cursor-pointer" onClick={() => setSearch("")} />
              </Badge>
            )}
            {filterStatus !== "all" && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Statut: {filterStatus === "paye" ? "Payées" : "En attente"}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterStatus("all")} />
              </Badge>
            )}
            {filterYear !== "all" && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Année: {filterYear}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterYear("all")} />
              </Badge>
            )}
            <span className="text-sm text-muted-foreground ml-2">
              {filteredCotisations.length} résultat(s)
            </span>
          </div>
        )}
      </div>

      {/* Table */}
      {filteredCotisations.length > 0 ? (
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F9F7F5]">
                    <TableHead className="font-semibold text-secondary">Food Truck</TableHead>
                    <TableHead className="font-semibold text-secondary">Année</TableHead>
                    <TableHead className="font-semibold text-secondary">Montant</TableHead>
                    <TableHead className="font-semibold text-secondary">Statut</TableHead>
                    <TableHead className="font-semibold text-secondary">Date paiement</TableHead>
                    <TableHead className="font-semibold text-secondary">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCotisations.map((c) => (
                    <TableRow key={c.id} className="hover:bg-[#F9F7F5]" data-testid={`cotisation-row-${c.id}`}>
                      <TableCell className="font-medium">{c.nom_food_truck}</TableCell>
                      <TableCell>{c.annee}</TableCell>
                      <TableCell><MontantCotisation cotisation={c} onDone={loadCotisations} /></TableCell>
                      <TableCell>
                        <Badge
                          variant={c.statut === "non_paye" ? "secondary" : "default"}
                          className={
                            c.statut === "offert" 
                              ? "bg-highlight" 
                              : c.statut === "paye" 
                                ? "bg-status-success" 
                                : "bg-status-warning text-white"
                          }
                        >
                          {c.statut === "offert" ? (
                            <><Gift className="w-3 h-3 mr-1" /> Offert</>
                          ) : c.statut === "paye" ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Payée</>
                          ) : (
                            <><Clock className="w-3 h-3 mr-1" /> Non payée</>
                          )}
                        </Badge>
                        {c.note && (
                          <p className="text-xs text-muted-foreground mt-1">{c.note}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {c.date_paiement
                          ? format(parseISO(c.date_paiement), "dd/MM/yyyy")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {c.statut === "non_paye" && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handlePayOnline(c)}
                              disabled={payingOnline === c.id}
                              className="bg-primary hover:bg-primary-hover"
                              data-testid={`pay-online-${c.id}`}
                            >
                              {payingOnline === c.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <><CreditCard className="w-4 h-4 mr-1" /> Payer</>
                              )}
                            </Button>
                            {isAdmin() && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setPayTarget(c.id)}
                                className="text-status-success hover:text-status-success/80"
                                data-testid={`pay-cotisation-${c.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden p-4 space-y-3">
              {filteredCotisations.map((c) => (
                <div
                  key={c.id}
                  className="p-4 bg-[#F9F7F5] rounded-lg"
                  data-testid={`cotisation-card-${c.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Truck className="w-5 h-5 text-primary" />
                      <span className="font-semibold text-secondary">{c.nom_food_truck}</span>
                    </div>
                    <Badge
                      variant={c.statut === "non_paye" ? "secondary" : "default"}
                      className={
                        c.statut === "offert" 
                          ? "bg-highlight" 
                          : c.statut === "paye" 
                            ? "bg-status-success" 
                            : "bg-status-warning text-white"
                      }
                    >
                      {c.statut === "offert" ? "Offert" : c.statut === "paye" ? "Payée" : "Non payée"}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Année:</span>
                      <span className="ml-2 font-medium">{c.annee}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Montant:</span>
                      <MontantCotisation cotisation={c} onDone={loadCotisations} />
                    </div>
                  </div>
                  {c.note && (
                    <p className="text-xs text-muted-foreground mt-2">{c.note}</p>
                  )}
                  {isAdmin() && c.statut === "non_paye" && (
                    <Button
                      size="sm"
                      className="w-full mt-3 bg-status-success hover:bg-status-success/90"
                      onClick={() => setPayTarget(c.id)}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" /> Marquer comme payée
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-12 text-center">
            <Receipt className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl text-secondary mb-2">Aucune cotisation trouvée</h3>
            <p className="text-muted-foreground">
              {search || filterStatus !== "all"
                ? "Modifiez vos filtres"
                : "Les cotisations seront générées automatiquement"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Pay Confirmation */}
      <AlertDialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer le paiement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action marquera la cotisation comme payée et mettra à jour le solde du membre.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePay}
              className="bg-status-success hover:bg-status-success/90"
              data-testid="confirm-pay-btn"
            >
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
